import { type Cell, type Table, flexRender } from "@tanstack/react-table";
import cx from "classnames";

import { ChevronDownIcon } from "@moment/icons";
import { DropdownMenu } from "@moment/ui";

import { type AggregationFnOption, aggregationFns, noneOption } from "../grouping-aggregations";
import { type DataGridColumnDef, type ProcessedDataGridColumnDef } from "../useColumnState";
import { useSelectedAggregationFn } from "./useSelectedAggregationFn";

export interface Props<T> {
	table: Table<T>;
	cell: Cell<T, unknown>;
}

export function GroupedRowsColumnHeader<T, TData>({ table, cell }: Props<T>) {
	const { meta } = table.options;

	const columnDef = cell.column.columnDef as ProcessedDataGridColumnDef<T, TData>;

	const setAggregationFn = (fn: AggregationFnOption<TData> | undefined) => {
		const newColumnDef = {
			...columnDef,
			aggregationFnId: fn?.label,
			aggregationFn: fn?.value,
		} as DataGridColumnDef<unknown, unknown>;
		meta?.updateColumn?.(cell.column.id, newColumnDef);
	};

	const aggregationOptions = aggregationFns[columnDef.fieldType || "Auto"];

	const selectedAggregationFn = useSelectedAggregationFn<T, TData>(
		cell,
		columnDef,
		aggregationOptions
	);

	let groupingCellHeader: JSX.Element | string | null = null;
	if (cell.getIsGrouped()) {
		groupingCellHeader = (
			<div title={`Grouping by ${cell.column.id}`} className="truncate">
				<span className="font-medium text-secondary">{cell.column.id}</span>
			</div>
		);
	} else if (cell.getIsAggregated() && selectedAggregationFn.label !== "None") {
		const aggregationFn = columnDef.aggregationFn;
		if (typeof aggregationFn === "string") {
			groupingCellHeader =
				aggregationFn === "auto" ? null : <div className="text-sm">{aggregationFn}</div>;
		} else if (typeof aggregationFn === "function" && aggregationFn.name) {
			// Fall back to function's name if it exists. This is particularly useful for custom
			// aggregation functions provided by the user.
			groupingCellHeader = <div className="text-sm">{aggregationFn.name}</div>;
		} else {
			groupingCellHeader = <div className="text-sm">Custom</div>;
		}
	}

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<button
					key={cell.id}
					className={cx(
						"group/table-row-header",
						"text-tertiary",
						{
							"cursor-pointer hover:bg-button-normal-secondary-hover":
								!cell.getIsGrouped(),
						},
						// "border-r ",
						// "min-h-[2.5rem]",
						"h-8",
						cell.getIsGrouped() && "pl-6",
						cell.getIsGrouped() &&
							"flex flex-row items-center space-x-2 px-2 pb-2 pt-2",
						!cell.getIsGrouped() &&
							"flex flex-row items-center space-x-2 px-2 pb-2 pt-2"
					)}
					disabled={cell.getIsGrouped()}
					style={{
						width: cell.column.getSize(),
					}}
				>
					{groupingCellHeader && (
						<>
							<div className="flex shrink-0 flex-row items-center space-x-1 truncate text-sm">
								{groupingCellHeader}
							</div>
							<div
								className={cx("flex", "truncate", "space-x-2", "text-primary", {
									"font-normal": cell.getIsAggregated(),
									"items-center": cell.getIsGrouped(),
								})}
							>
								{flexRender(
									cell.getIsAggregated()
										? (cell.column.columnDef.aggregatedCell ??
												cell.column.columnDef.cell)
										: cell.column.columnDef.cell,
									cell.getContext()
								)}
							</div>
						</>
					)}

					<div
						className={cx("truncate px-1 mr-2 italic text-tertiary", {
							"hidden": cell.getIsGrouped() || groupingCellHeader,
							"invisible group-hover/aggregate:visible":
								cell.getIsGrouped() || !groupingCellHeader,
						})}
					>
						No summary
					</div>
					<div
						className={cx("flex flex-grow", {
							"hidden": cell.getIsGrouped() || groupingCellHeader,
							"invisible group-hover/aggregate:visible":
								cell.getIsGrouped() || !groupingCellHeader,
						})}
					>
						<ChevronDownIcon className="ml-auto fill-icon-default" size={16} />
					</div>
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="w-56 text-xs" align="start">
					<DropdownMenu.RadioGroup
						value={selectedAggregationFn.label ?? "None"}
						onValueChange={(value) => {
							const selected = (aggregationOptions.get(value) ||
								noneOption) as AggregationFnOption<any>;
							if (value === "None") {
								setAggregationFn(noneOption);
							} else {
								setAggregationFn(selected);
							}
						}}
					>
						<DropdownMenu.RadioItem value="None" level="secondary">
							<div className="italic group-hover/aggregate:text-inverse-primary">
								None
							</div>
						</DropdownMenu.RadioItem>

						{/* <DropdownMenu.Separator />
						<DropdownMenu.Label>Custom aggregations</DropdownMenu.Label>

						<DropdownMenu.RadioItem value="percent-unique">
							<div>Error count</div>
						</DropdownMenu.RadioItem>

						<DropdownMenu.Separator />
						<DropdownMenu.Label>Built-in aggregations</DropdownMenu.Label> */}

						{[...aggregationOptions.values()].map((aggregationFn) => (
							<DropdownMenu.RadioItem
								key={aggregationFn.label}
								value={aggregationFn.label}
							>
								<div>{aggregationFn.label}</div>
							</DropdownMenu.RadioItem>
						))}

						{/* <DropdownMenu.RadioItem value="emptyCount">
							<div>Empty</div>
						</DropdownMenu.RadioItem>
						<DropdownMenu.RadioItem value="filledCount">
							<div>Filled</div>
						</DropdownMenu.RadioItem>
						<DropdownMenu.RadioItem value="uniqueCount">
							<div>Unique</div>
						</DropdownMenu.RadioItem>
						<DropdownMenu.RadioItem value="percent-empty">
							<div>Percent Empty</div>
						</DropdownMenu.RadioItem>
						<DropdownMenu.RadioItem value="percent-filled">
							<div>Percent Filled</div>
						</DropdownMenu.RadioItem>
						<DropdownMenu.RadioItem value="percent-unique">
							<div>Percent Unique</div>
						</DropdownMenu.RadioItem> */}
					</DropdownMenu.RadioGroup>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}
